import { memo, useCallback } from 'react'
import { X } from 'lucide-react'
import Select, { SingleValue } from 'react-select'
import { cn } from '@global/utils/tailwind'
import { Button } from '@taxonomy-translation/components'
import { FileObjective } from '../types'

interface FileCardOption {
  value: FileObjective
  label: string
}

interface FileCardProps {
  title: string
  subtitle?: string
  objective?: string
  className?: string
  isError?: boolean
  isDisabled?: boolean
  onObjectiveChange?: (value: FileObjective) => void
  onRemoveClick?: () => void
}

export const FileCard = memo(
  ({
    title,
    subtitle,
    objective,
    className,
    isError,
    isDisabled = false,
    onObjectiveChange,
    onRemoveClick,
  }: FileCardProps) => {
    const onTypeChangeHandler = useCallback(
      (newValue: SingleValue<FileCardOption>) => {
        if (newValue) {
          onObjectiveChange?.(newValue.value)
        }
      },
      [onObjectiveChange],
    )
    const indicatorSeparator = useCallback(() => ({ display: 'none' }), [])

    return (
      <div
        className={cn(
          'border rounded-lg h-[70px] px-4 flex items-center justify-between',
          { 'border-red-500': isError },
          { 'border-gray-300 ': !isError },
          className,
        )}
      >
        <div className="flex flex-col grow">
          <p className="text-sm text-slate-800">{title}</p>
          <p className="text-sm text-slate-400">{subtitle}</p>
        </div>

        {/* TODO: change to normal select component from shadcn when lib instaled */}

        {!!(objective || onObjectiveChange) && (
          <Select
            isDisabled={isDisabled}
            defaultValue={FILE_CARD_OPTIONS.find(option => option.value === objective)}
            className="h-10 w-[11.5rem] rounded-md mr-2"
            options={FILE_CARD_OPTIONS}
            styles={{ indicatorSeparator }}
            onChange={onTypeChangeHandler}
          />
        )}

        {!!onRemoveClick && (
          <Button variant="text" disabled={isDisabled} onClick={onRemoveClick}>
            <X className="size-4" />
          </Button>
        )}
      </div>
    )
  },
)

FileCard.displayName = 'FileCard'

const FILE_CARD_OPTIONS: FileCardOption[] = [
  { value: FileObjective.DATA_DICTIONARY, label: 'Data dictionary' },
  { value: FileObjective.LIST_VALUES, label: 'List values' },
]
